import { Edge, useReactFlow } from "@xyflow/react";
import { getEdgeId } from "features/entities/EntityCreatePage/EntityGraph/edges/edgeUtils";
import { DRAFT_NODE_ID } from "features/entities/EntityCreatePage/EntityGraph/entityGraphConstants";
import { useAddDraftNode } from "features/entities/EntityCreatePage/EntityGraph/hooks/useAddDraftNode";
import { useFitViewWithDelay } from "features/entities/EntityCreatePage/EntityGraph/hooks/useFitViewWithDelay";
import { GraphRef } from "features/entities/EntityCreatePage/EntityGraph/types";
import { ForwardedRef, useCallback, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "shared/components/toast/useToast";

export const useImperativeGraphRef = (ref: ForwardedRef<GraphRef>) => {
  const { addEdges, setNodes, setEdges } = useReactFlow();
  const toast = useToast();

  const fitViewWithDelay = useFitViewWithDelay();

  const addDraftNode = useAddDraftNode();

  const removeDraftNode = useCallback(() => {
    setNodes((nodes) => nodes.filter((node) => node.id !== DRAFT_NODE_ID));
    setEdges((edges) => edges.filter((edge) => edge.source !== DRAFT_NODE_ID));
    fitViewWithDelay();
  }, [setNodes, setEdges, fitViewWithDelay]);

  const { t } = useTranslation(["common", "entity"]);

  const addDraftEdge = useCallback(
    (investingEntity: string, investedIntoEntity: string) => {
      if (
        investingEntity !== DRAFT_NODE_ID &&
        investedIntoEntity !== DRAFT_NODE_ID
      ) {
        toast.showToast({
          message: t("entity:create.toasts.only_draft_connect"),
          variant: "warning",
        });

        return;
      }

      const newEdge: Edge = {
        id: getEdgeId(investingEntity, investedIntoEntity),
        source: investingEntity,
        target: investedIntoEntity,
        animated: true,
      };
      addEdges(newEdge);
    },
    [addEdges, t, toast],
  );

  useImperativeHandle(ref, () => ({
    addDraftNode,
    removeDraftNode,
    addDraftEdge,
    fitViewWithDelay,
  }));
};
